import React, { useState } from 'react';
import {
  Datagrid, TextField, useNotify, usePermissions,
} from 'react-admin';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import * as yup from 'yup';
import useForm from '../../../../hooks/useForm';
import CustomModal from '../../CustomModal';
import EditFixRateForm from '../../Forms/EditFixRateForm';
import { EditRateForm, ROLES, YupSchemaObject } from '../../../../utils/types';
import { RoleRateItem } from '../../../../business_logic/models/roleRateEntry';
import HistoryRatesManagerTable from '../HistoryRatesManager';
import RowActions from '../RowActions';
import useRolesData from '../../../../hooks/useRolesData';
import CustomCurrencyField from '../CustomFields/CustomCurrencyField';

const dataGridStyles = {
  width: '85vw',
  maxWidth: '950px',
  '& .RaDatagrid-headerCell': {
    padding: '16px',
    height: 'auto',
  },
  '& .column-actions': {
    textAlign: 'center',
  },
  '& .column-standardRole': {
    maxWidth: 'calc(100vw/6)',
    lineHeight: '.7rem',
  },
};

type ListRatesProps = {
  setData: React.Dispatch<React.SetStateAction<RoleRateItem[]>>;
};

const ListRates: React.FC<ListRatesProps> = ({ setData }) => {
  const notify = useNotify();
  const { updateRoleRate } = useRolesData();
  const { permissions, isLoading: isLoadingPermissions } = usePermissions();
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isOpenHistoryModal, setOpenHistoryModal] = useState(false);
  const [historyModalTitle, setHistoryModalTitle] = useState('');
  const [pointerRolRateId, setPointerRolRateId] = useState('');
  const [open, setOpen] = useState(false);
  const [originalRackRate, setOriginalRackRate] = useState<number | undefined>(0);
  const [fullWidth] = React.useState(true);

  const defaultEditRoleFormValues: EditRateForm = {
    id: '',
    standardRole: '',
    description: '',
    rate: NaN,
  };

  const validationSchema: YupSchemaObject<EditRateForm> = {
    id: yup.string().required(),
    standardRole: yup.string().required(),
    description: yup.string().required('Description is required'),
    rate: yup
      .number()
      .transform((value) => (Number.isNaN(value) ? 0 : value))
      .optional()
      .min(0, 'Enter a valid rate (only positive numbers are allowed)'),
  };

  const updateRoleRateList = (editedRole: EditRateForm) => {
    setData((roleRateList) => {
      const updatedRoleRateList = roleRateList.map((roleRate) =>
        (roleRate.id === editedRole.id
          ? {
            ...roleRate,
            rate: editedRole.rate,
          }
          : roleRate));
      return [...updatedRoleRateList];
    });
  };

  const {
    formValues: editRoleFormValues,
    handleInputChange: handleEditRoleInputChanges,
    reset: resetEditRoleForm,
    setFormValues: setEditRoleFormValues,
    errors: editRoleFormErrors,
    setErrors: setEditRoleFormErrors,
  } = useForm(defaultEditRoleFormValues, validationSchema);

  const editUserModalProps = {
    title: 'Edit rate',
    description: 'Please, edit the rack rate for the standard role: ',
    isEditModal: true,
    standardRoleName: editRoleFormValues?.standardRole,
  };

  const HistoryratesManagerProps = {
    titleHistoryModal: 'Rates History',
    title: `${historyModalTitle}`,
    cancelButtonText: 'Close',
  };

  const isEditAvailable = permissions?.roles?.includes(ROLES.rackRatesEditor);

  const checkRackRateValues = (updatedRoleData: EditRateForm) =>
    Number.isNaN(updatedRoleData.rate)
    || (originalRackRate && originalRackRate === updatedRoleData.rate);

  const handleUpdateRoleRate = async () => {
    const updatedRoleData = editRoleFormValues;
    if (checkRackRateValues(editRoleFormValues)) {
      editRoleFormValues.rate = Number.NaN;
      notify('The rack rates values should be changed', { type: 'error' });
      return;
    }
    try {
      await updateRoleRate(editRoleFormValues);
      updateRoleRateList(updatedRoleData);
      resetEditRoleForm();
      setOpenEditModal(false);
      notify('Rack rate successfully updated', { type: 'success' });
    } catch (error) {
      notify('Error on updating rack rate', { type: 'error' });
    }
  };

  const handleEditClick = ({ rate, ...record }: RoleRateItem) => {
    setEditRoleFormValues({
      ...record,
      description: '',
      rate: rate ?? NaN,
    });
    setOriginalRackRate(rate);
    setOpenEditModal(true);
    setEditRoleFormErrors({});
  };

  const handleHistoryClick = ({ id, standardRole }: RoleRateItem) => {
    setHistoryModalTitle(standardRole);
    setOpenHistoryModal(true);
    setPointerRolRateId(id.toString());
  };

  const isDisableSubmitEditForm = Boolean(
    !editRoleFormValues.id || !editRoleFormValues.description || !editRoleFormValues.rate,
  );

  const handleClickOpen = () => {
    if (checkRackRateValues(editRoleFormValues)) {
      editRoleFormValues.rate = Number.NaN;
      setOpenEditModal(false);
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleExit = () => {
    editRoleFormValues.rate = Number.NaN;
    setOpen(false);
    setOpenEditModal(false);
  };

  window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    if (!checkRackRateValues(editRoleFormValues)) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  return (
    <>
      <Datagrid
        sx={dataGridStyles}
        bulkActionButtons={false}
        size='small'
        isLoading={isLoadingPermissions}
      >
        <TextField source='standardRole' label='Standard role' />
        <CustomCurrencyField source='rate' label='Rate' />
        <TextField source='typeOfRate' label='Type of rate' />

        <RowActions
          label='Actions'
          source='actions'
          actions={[
            {
              actionType: 'editRackRateValues',
              onClick: handleEditClick,
              hidden: !isEditAvailable,
            },
            {
              actionType: 'history',
              onClick: handleHistoryClick,
            },
          ]}
        />
      </Datagrid>

      <CustomModal
        {...editUserModalProps}
        open={openEditModal}
        handleClose={() => handleClickOpen()}
        handleConfirm={handleUpdateRoleRate}
        handleCancel={() => handleClickOpen()}
        disabledConfirmButton={isDisableSubmitEditForm}
        variant='outlined'
        confirmButtonColor='primary'
      >
        <EditFixRateForm
          formValues={editRoleFormValues}
          handleInputChange={handleEditRoleInputChanges}
          errors={editRoleFormErrors}
        />
      </CustomModal>

      <CustomModal
        {...HistoryratesManagerProps}
        width='auto'
        open={isOpenHistoryModal}
        handleClose={() => setOpenHistoryModal(false)}
        handleCancel={() => setOpenHistoryModal(false)}
        variant='outlined'
        confirmButtonColor='success'
      >
        <HistoryRatesManagerTable pointerRolRateId={pointerRolRateId} />
      </CustomModal>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={fullWidth}
        maxWidth='xs'
        aria-labelledby='responsive-dialog-title'
      >
        <DialogTitle sx={{ ml: 16 }} id='responsive-dialog-title'>
          Unsaved changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ ml: 15 }}>Your changes will be lost</DialogContentText>
          <DialogContentText sx={{ ml: 12 }}>Are you sure you want to leave?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleExit}
            variant='outlined'
            color='primary'
            startIcon={<CheckCircleIcon />}
            autoFocus
          >
            Leave
          </Button>
          <Button
            autoFocus
            onClick={handleClose}
            variant='outlined'
            color='primary'
            startIcon={<CancelIcon />}
          >
            Continue editing
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ListRates;
